import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import DragRotate from '../utils/DragRotate.js';

export default class SingleModel {
  constructor(element) {
    this.element = element;

    // Canvas placé à l'extérieur de ScrollSmoother
    this.canvas = document.querySelector('.model-layer .webgl');

    // Les quatre destinations restent dans le main
    this.targets = [...this.element.querySelectorAll('[data-model-target]')];

    this.settings = {
      // Modèle défini dans le HTML (data-model sur le <main>), avec un modèle par défaut
      modelPath: element.dataset.model || './assets/models/ac/scene.gltf',
      modelFill: 0.85,
      smoothness: 8,
      rotationSpeed: 0.12,
      transitionRotation: Math.PI * 0.35,
    };

    this.sizes = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    this.scene = new THREE.Scene();
    this.clock = new THREE.Clock();

    this.model = null;
    this.root = new THREE.Group();
    this.scene.add(this.root);

    this.current = {
      x: 0,
      y: 0,
      scale: 1,
      rotation: 0,
    };

    this.destination = { ...this.current };

    this.userYaw = 0; // rotation ajoutée à la souris
    this.isReady = false;
    this.isDestroyed = false;
    this.animationFrame = null;

    this.init();
  }

  init() {
    if (!this.canvas || this.targets.length === 0) {
      console.warn('SingleModel : canvas ou zones 3D introuvables.', {
        canvas: this.canvas,
        targets: this.targets.length,
      });
      return;
    }

    this.createCamera();
    this.createLights();
    this.createRenderer();

    // Rotation du modèle à la souris (clic maintenu + glisser)
    this.dragRotate = new DragRotate({
      hitTest: (x, y) => this.isOverModel(x, y),
    });

    this.handleAnimation = this.animate.bind(this);
    this.handleResize = this.resize.bind(this);

    window.addEventListener('resize', this.handleResize);

    // Calculer la position initiale
    this.updateDestination();
    Object.assign(this.current, this.destination);

    // Démarrer le rendu avant le chargement du modèle
    this.animate();

    this.loadModel();
  }

  // =====================================================
  // Caméra
  // =====================================================

  createCamera() {
    const { width, height } = this.sizes;

    this.camera = new THREE.OrthographicCamera(
      -width / 2,
      width / 2,
      height / 2,
      -height / 2,
      0.1,
      2000,
    );

    this.camera.position.set(0, 0, 1000);
    this.camera.lookAt(0, 0, 0);
  }

  // =====================================================
  // Éclairage
  // =====================================================

  createLights() {
    const ambient = new THREE.AmbientLight(0xffffff, 2);
    this.scene.add(ambient);

    const main = new THREE.DirectionalLight(0xffffff, 3);
    main.position.set(300, 500, 600);
    this.scene.add(main);

    const accent = new THREE.DirectionalLight(0x847aa9, 2);
    accent.position.set(-300, 100, 400);
    this.scene.add(accent);
  }

  // =====================================================
  // Renderer
  // =====================================================

  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
    });

    this.renderer.setSize(this.sizes.width, this.sizes.height);

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.3;
  }

  // =====================================================
  // Chargement du modèle GLTF
  // =====================================================

  loadModel() {
    const loader = new GLTFLoader();

    loader.load(
      this.settings.modelPath,

      (gltf) => {
        if (this.isDestroyed) return;

        this.model = gltf.scene;

        const box = new THREE.Box3().setFromObject(this.model);

        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        const maxDimension = Math.max(size.x, size.y, size.z);

        if (maxDimension <= 0) {
          console.error('SingleModel : le modèle GLTF est vide.');
          return;
        }

        // Recentrer le modèle
        this.model.position.sub(center);

        // Normaliser le modèle à une unité
        const normalization = 1 / maxDimension;

        this.model.scale.setScalar(normalization);
        this.model.position.multiplyScalar(normalization);

        this.root.add(this.model);

        this.isReady = true;

        console.log('SingleModel : modèle GLTF chargé.', this.model);

        this.updateDestination();
        Object.assign(this.current, this.destination);
      },

      undefined,

      (error) => {
        console.error('SingleModel : erreur de chargement GLTF :', error);
      },
    );
  }

  // =====================================================
  // Position d'une zone dans la fenêtre
  // =====================================================

  getTargetData(target) {
    const rect = target.getBoundingClientRect();

    return {
      // Centre horizontal dans l'espace Three.js
      x: rect.left + rect.width / 2 - this.sizes.width / 2,

      // Centre vertical à l'écran
      screenY: rect.top + rect.height / 2,

      // Taille souhaitée du modèle
      scale: Math.min(rect.width, rect.height) * this.settings.modelFill,
    };
  }

  // Le pointeur est-il au-dessus du modèle ? (le canvas ne capte pas la souris)
  isOverModel(x, y) {
    if (!this.isReady) return false;

    const centerX = this.sizes.width / 2 + this.current.x;
    const centerY = this.sizes.height / 2 - this.current.y;
    const half = this.current.scale / 2;

    return Math.abs(x - centerX) <= half && Math.abs(y - centerY) <= half;
  }

  // =====================================================
  // Déplacement du modèle pendant le scroll
  // =====================================================

  updateDestination() {
    if (!this.targets.length) return;

    const positions = this.targets.map((target) => this.getTargetData(target));

    const viewportCenter = this.sizes.height / 2;

    let first = positions[0];
    let second = positions[0];
    let progress = 0;
    let index = 0;

    // Les centres des zones descendent dans le tableau
    // mais leur position à l'écran diminue au scroll.

    if (positions[0].screenY >= viewportCenter) {
      // Avant la première destination
      first = second = positions[0];
    } else if (positions[positions.length - 1].screenY <= viewportCenter) {
      // Après la dernière destination
      first = second = positions[positions.length - 1];
      index = positions.length - 1;
    } else {
      // Entre deux destinations
      for (let i = 0; i < positions.length - 1; i++) {
        const a = positions[i];
        const b = positions[i + 1];

        if (a.screenY <= viewportCenter && b.screenY >= viewportCenter) {
          first = a;
          second = b;
          index = i;

          const distance = b.screenY - a.screenY;

          progress = distance > 0 ? (viewportCenter - a.screenY) / distance : 0;

          break;
        }
      }
    }

    // Courbe d'accélération et décélération
    const t = progress * progress * (3 - 2 * progress);

    // Mouvement horizontal entre les zones
    this.destination.x = THREE.MathUtils.lerp(first.x, second.x, t);

    // Position verticale du modèle
    const screenY = THREE.MathUtils.lerp(first.screenY, second.screenY, t);

    this.destination.y = this.sizes.height / 2 - screenY;

    // Taille adaptée à la zone
    this.destination.scale = THREE.MathUtils.lerp(first.scale, second.scale, t);

    // Rotation progressive
    this.destination.rotation = (index + t) * this.settings.transitionRotation;
  }

  // =====================================================
  // Animation
  // =====================================================

  animate() {
    if (this.isDestroyed) return;

    this.animationFrame = requestAnimationFrame(this.handleAnimation);

    const delta = Math.min(this.clock.getDelta(), 0.05);

    // Indispensable avec GSAP ScrollSmoother :
    // récupérer les positions visuelles à chaque frame.
    this.updateDestination();

    const smooth = 1 - Math.exp(-this.settings.smoothness * delta);

    this.current.x = THREE.MathUtils.lerp(
      this.current.x,
      this.destination.x,
      smooth,
    );

    this.current.y = THREE.MathUtils.lerp(
      this.current.y,
      this.destination.y,
      smooth,
    );

    this.current.scale = THREE.MathUtils.lerp(
      this.current.scale,
      this.destination.scale,
      smooth,
    );

    this.current.rotation = THREE.MathUtils.lerp(
      this.current.rotation,
      this.destination.rotation,
      smooth,
    );

    // Appliquer les transformations
    this.root.position.set(this.current.x, this.current.y, 0);

    this.root.scale.setScalar(this.current.scale);

    // Rotation du scroll + rotation et inclinaison à la souris
    this.userYaw += this.dragRotate.update(delta);
    this.root.rotation.x = this.dragRotate.tilt;
    this.root.rotation.y = this.current.rotation + this.userYaw;

    // Rotation légère permanente du modèle (en pause pendant le glissement)
    if (this.model && this.isReady && !this.dragRotate.dragging) {
      this.model.rotation.y += delta * this.settings.rotationSpeed;
    }

    this.renderer.render(this.scene, this.camera);
  }

  // =====================================================
  // Responsive
  // =====================================================

  resize() {
    this.sizes.width = window.innerWidth;
    this.sizes.height = window.innerHeight;

    this.camera.left = -this.sizes.width / 2;
    this.camera.right = this.sizes.width / 2;
    this.camera.top = this.sizes.height / 2;
    this.camera.bottom = -this.sizes.height / 2;

    this.camera.updateProjectionMatrix();

    this.renderer.setSize(this.sizes.width, this.sizes.height);

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    this.updateDestination();
  }

  // =====================================================
  // Nettoyage
  // =====================================================

  destroy() {
    this.isDestroyed = true;

    cancelAnimationFrame(this.animationFrame);

    window.removeEventListener('resize', this.handleResize);
    this.dragRotate?.destroy();

    if (this.model) {
      this.model.traverse((child) => {
        if (!child.isMesh) return;

        child.geometry?.dispose();

        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];

        materials.forEach((material) => {
          material?.dispose();
        });
      });
    }

    this.renderer?.dispose();
  }
}
